/* eslint complexity: ["error", 10] -- this applies for the entire file */
import type { RefObject } from "react";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

import type { SourceLoadEventPayload, VideoContentFit } from "expo-video";
import type { ViewStyle } from "react-native";

import { ExpoVideoPlayer } from "@/ui/composed/media/videoPlayer/adapter/ExpoVideoPlayer";
import type {
  ExpoVideoPlayerHandle,
  PlaybackRateChangePayload,
  PlayingChangePayload,
  TimeUpdatePayload,
  VideoNaturalSize,
  VideoPlaybackStatus,
} from "@/ui/composed/media/videoPlayer/adapter/ExpoVideoPlayer";
import { Spinner } from "@/ui/core";
import { Box } from "@/ui/layout";

interface BaseVideoPlayerProps extends ViewWithTestID {
  onError: (error: string) => void;
  onPlaybackRateChange?: (payload: PlaybackRateChangePayload) => void;
  onPlaybackStatusUpdate?: (status: VideoPlaybackStatus) => void;
  onPlayingChange?: (payload: PlayingChangePayload) => void;
  onSourceLoad?: (payload: SourceLoadEventPayload) => void;
  onTimeUpdate?: (payload: TimeUpdatePayload) => void;
  onUnloadRef?: RefObject<() => void>;
  onVideoSize?: (size: VideoNaturalSize) => void;
  progressUpdateIntervalMillis: number;
  resizeMode?: VideoContentFit;
  shouldAutoPlay?: boolean;
  shouldShowControls?: boolean;
  shouldShowThumbnail?: boolean;
  size: SizeDimensions;
  styles?: ViewStyle;
  thumbnailUrl?: string;
  videoUrl: string;
}
// Alias maintained so existing imports still compile
type BaseVideoPlayerHandle = ExpoVideoPlayerHandle;

const BaseVideoPlayer = forwardRef<BaseVideoPlayerHandle, BaseVideoPlayerProps>(
  (
    // eslint-disable-next-line no-restricted-syntax -- this component relies on multiple props related to video tracking
    {
      onError,
      onPlaybackRateChange,
      onPlaybackStatusUpdate,
      onPlayingChange,
      onSourceLoad,
      onTimeUpdate,
      onUnloadRef,
      onVideoSize,
      progressUpdateIntervalMillis,
      resizeMode = "contain",
      shouldAutoPlay = false,
      shouldShowControls = true,
      shouldShowThumbnail = false,
      size,
      styles,
      testID,
      thumbnailUrl,
      videoUrl,
    }: BaseVideoPlayerProps,
    ref,
    // eslint-disable-next-line max-params
  ) => {
    const [isLoading, setIsLoading] = useState(true);
    const expoVideoRef = useRef<ExpoVideoPlayerHandle | null>(null);

    const shouldShowLoader = isLoading && !shouldShowThumbnail;

    // even though refs are being used, we invoke useCallback to memoize these functions,
    // since technically there could be no ref passed in, and later a ref passed in
    // or there could be a ref passed in, and later no ref passed in
    const onUnloadCallback = useCallback(() => {
      onUnloadRef?.current();
    }, [onUnloadRef]);

    useEffect(() => {
      return () => {
        onUnloadCallback();
      };
    }, [onUnloadCallback]);

    const handlePlaybackStatusUpdate = (status: VideoPlaybackStatus) => {
      setIsLoading(false);
      onPlaybackStatusUpdate?.(status);
    };

    const thumbUri =
      typeof thumbnailUrl === "string" ? thumbnailUrl : undefined;
    const resizeModeValue: VideoContentFit = resizeMode;
    // Keeping the forwarded ref in sync on each render. It makes the child video player’s ref available in two places: inside the component (for internal logic) and to the parent (so the parent can call play/pause). That helper just syncs both to the same underlying player instance, handling either a function ref or an object ref.
    const assignRef = (node: ExpoVideoPlayerHandle | null) => {
      expoVideoRef.current = node;
      if (!ref) return;
      if (typeof ref === "function") {
        ref(node);
      } else if ("current" in ref) {
        const mutableRef = ref;
        mutableRef.current = node;
      }
    };

    return (
      <Box
        height={size.height}
        justifyContent="center"
        testID={testID}
        width={size.width}
      >
        <ExpoVideoPlayer
          contentFit={resizeModeValue}
          onError={onError}
          onFirstFrame={() => setIsLoading(false)}
          onPlaybackRateChange={onPlaybackRateChange}
          onPlayingChange={onPlayingChange}
          onSourceLoad={onSourceLoad}
          onTimeUpdate={onTimeUpdate}
          onVideoSize={onVideoSize}
          onVideoStatusUpdate={handlePlaybackStatusUpdate}
          progressUpdateIntervalMillis={progressUpdateIntervalMillis}
          ref={assignRef}
          shouldAutoPlay={shouldAutoPlay}
          shouldShowControls={shouldShowControls}
          shouldUseThumbnail={shouldShowThumbnail}
          sourceUri={videoUrl}
          style={{
            backgroundColor: "transparent",
            height: isLoading ? 0 : "100%",
            width: "100%",
            ...(styles || {}),
          }}
          testID={`${testID}-expo-vplayer`}
          thumbnailUri={thumbUri}
        />
        {shouldShowLoader && <Spinner />}
      </Box>
    );
  },
);
BaseVideoPlayer.displayName = "BaseVideoPlayer";

export type { BaseVideoPlayerHandle, BaseVideoPlayerProps };
export default BaseVideoPlayer;
